import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import { getHeaders, getUrl } from "../../../../api/services/defs/internal-api/internal-base-request";

import { meetingDetailsQueryKeys } from "../query-keys";

type MeetingEvent = {
	type: string;
	meetingId: string;
	entityId: string;
	originUserId: string;
	timestamp: string;
	changes?: Record<string, unknown>;
};

const MAX_RETRY_ATTEMPTS = 5;
const RETRY_DELAYS = [1000, 2000, 4000, 8000, 16000];

/**
 * Hook that subscribes to real-time meeting events via SSE.
 * When agenda or action items change on another participant's screen,
 * this hook invalidates the relevant React Query caches to trigger a refetch.
 * Events from the current user are skipped (already reflected via optimistic updates).
 */
export const useMeetingEvents = (meetingId: string, userId: string): void => {
	const queryClient = useQueryClient();
	const abortControllerRef = useRef<AbortController | null>(null);
	const retryCountRef = useRef(0);
	const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const isMountedRef = useRef(true);

	useEffect(() => {
		isMountedRef.current = true;

		if (!meetingId) return;

		const handleEvent = (data: MeetingEvent): void => {
			if (data.originUserId === userId) return;

			if (data.type.startsWith("agenda_item_")) {
				queryClient.invalidateQueries({
					queryKey: meetingDetailsQueryKeys.meetingAgendaItems(meetingId),
				});
			} else if (data.type.startsWith("action_item_")) {
				queryClient.invalidateQueries({
					queryKey: meetingDetailsQueryKeys.meetingActionItems(meetingId),
				});
			}
		};

		const connectToStream = async (): Promise<void> => {
			if (!isMountedRef.current) return;
			if (retryCountRef.current >= MAX_RETRY_ATTEMPTS) return;

			abortControllerRef.current = new AbortController();

			try {
				const url = getUrl(`/v1/meetings/meetings/${meetingId}/events`);
				const headers = getHeaders();

				const response = await fetch(url, {
					headers: headers as Record<string, string>,
					signal: abortControllerRef.current.signal,
				});

				if (!response.ok || !response.body) {
					throw new Error(`SSE connection failed: ${response.statusText}`);
				}

				if (retryCountRef.current > 0) {
					retryCountRef.current = 0;

					// Reconcile any missed updates on reconnect
					queryClient.invalidateQueries({
						queryKey: meetingDetailsQueryKeys.meetingAgendaItems(meetingId),
					});
					queryClient.invalidateQueries({
						queryKey: meetingDetailsQueryKeys.meetingActionItems(meetingId),
					});
				} else {
					retryCountRef.current = 0;
				}

				const reader = response.body.getReader();
				const decoder = new TextDecoder();
				let buffer = "";

				while (isMountedRef.current) {
					const { done, value } = await reader.read();
					if (done) break;

					buffer += decoder.decode(value, { stream: true });

					const messages = buffer.split("\n\n");
					buffer = messages.pop() || "";

					for (const message of messages) {
						let eventType = "";
						let eventData = "";

						for (const line of message.split("\n")) {
							if (line.startsWith("event:")) {
								eventType = line.substring(6).trim();
							} else if (line.startsWith("data:")) {
								eventData = line.substring(5).trim();
							}
						}

						if (eventType === "change" && eventData) {
							try {
								const parsed: MeetingEvent = JSON.parse(eventData);
								handleEvent(parsed);
							} catch {
								// Ignore malformed events
							}
						}
					}
				}
			} catch (err) {
				if (err instanceof Error && err.name === "AbortError") return;
				if (!isMountedRef.current) return;

				const delay = RETRY_DELAYS[Math.min(retryCountRef.current, RETRY_DELAYS.length - 1)];
				retryCountRef.current += 1;

				retryTimeoutRef.current = setTimeout(() => {
					if (isMountedRef.current) {
						connectToStream().catch(() => {});
					}
				}, delay);
			}
		};

		connectToStream().catch(() => {});

		return () => {
			isMountedRef.current = false;
			abortControllerRef.current?.abort();
			if (retryTimeoutRef.current) {
				clearTimeout(retryTimeoutRef.current);
			}
		};
	}, [meetingId, userId, queryClient]);
};
